import React, { useEffect, useState, useRef, forwardRef } from 'react';
import ReactDOM from 'react-dom';
import { useDropdown } from '../dropdown/dropdown';

type DropdownPosition = 'top' | 'bottom' | 'right' | 'left';
export interface DatepickerDropdownMenuProps {
  className?: string;
  [others: string]: any;
  placement?: DropdownPosition;
  children?: any;
  renderAsPortal: boolean,
  overrideStyles: any,
  }

  const DatepickerDropdownMenu = forwardRef<HTMLUListElement, DatepickerDropdownMenuProps>(
    ({ className, children, placement = 'bottom', renderAsPortal: propRenderAsPortal = false, overrideStyles: propOverrideStyles = null, ...rest }, ref) => {
    const { id, showMenu, setShowMenu, dropdownRef, handleClickMenu, keepShown, onChangeToggleMenu, renderAsPortal: hookRenderAsPortal, overrideStyles: hookOverrideStyles = null } = useDropdown();
  const [theme, setTheme] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0, width: 0 });
  const [menuWidth, setMenuWidth] = useState(0);
  const menuRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    const themes = ['lhh-2024', 'lhh-2025', 'lhh-JP', 'adecco', 'adecco-nl', 'akkodis', 'tagcom'];
    
    const foundTheme = themes.find(theme => document.querySelector(`.${theme}`) !== null);
    if (foundTheme) {
      setTheme(foundTheme);
    }
  }, []);

  useEffect(() => {
    if (!dropdownRef.current || !menuRef.current) return;

      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as Node | null;
        if (!dropdownRef.current?.contains(target) && !menuRef.current?.contains(target)) {
          setShowMenu(false);
          if (onChangeToggleMenu) {
            onChangeToggleMenu(false);
          }
        }
      };

      if (showMenu) {
        document.addEventListener('click', handleClickOutside);
      }

      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
  }, [showMenu, dropdownRef, menuRef]);

  useEffect(() => {
    if (!dropdownRef.current) return;
    if (!showMenu) return;
  
    const updateWidth = () => {
      if (dropdownRef.current) {
        setMenuWidth(dropdownRef.current.offsetWidth);
      }
    };
  
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [showMenu]);
  
  useEffect(() => {
    if (!dropdownRef.current || !menuRef.current || !showMenu) return;
  
    const updatePosition = () => {
      if (!dropdownRef.current || !menuRef.current) return;
      
      const buttonRect: DOMRect = dropdownRef.current.getBoundingClientRect();
      const menuRect: DOMRect = menuRef.current.getBoundingClientRect();
  
      const spaceAbove: number = buttonRect.top;
      let newTop: number;

      // Determine if there's enough space above the button for the dropdown
      if (spaceAbove >= menuRect.height) {
        newTop = buttonRect.top - menuRect.height; // Place above if there is enough space
      } else {
        newTop = buttonRect.bottom; // Default to below if not enough space above
      }
  
      // Apply styles directly on the dropdown element
      menuRef.current.style.position = 'fixed';
      menuRef.current.style.top = `${newTop}px`;
    };
  
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
  
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition);
    };
  }, [showMenu, dropdownRef, menuRef]);
  
  const portaStyle: React.CSSProperties = {
    ...((propRenderAsPortal || hookRenderAsPortal) && {
      top: `${menuPosition.top}px`,
      left: `${menuPosition.left}px`,
      width: 'auto',
    })
  };

  const styles = { ...portaStyle, ...propOverrideStyles, ...hookOverrideStyles };

  const content = (
    <ul
    className={`tag-ds dropdown-menu ${theme ? theme : ''} ${showMenu ? '' : 'hidden'} ${className || ''}`}
    id={`${id || 'dropdown'}-menu`}
      ref={ref || menuRef}
      aria-labelledby={`${id}-btn`}
      onClick={!keepShown ? handleClickMenu : undefined}
      style={(propRenderAsPortal || hookRenderAsPortal) ? styles : {}}
      {...rest}
    >
      {children}
    </ul>
  );

  if (showMenu && (propRenderAsPortal || hookRenderAsPortal)) {
    return ReactDOM.createPortal(content, document.body);
  }
  return content;
}
);

export default DatepickerDropdownMenu;